import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useAccountAdminInformation } from '../../../views/account/admin/common/hook/query/useAccountAdminInformation';

import PageTemplate from '../../../components/template/PageTemplate';
import AdminFormNavbar from '../../../views/account/admin/form/AdminFormNavbar';
import AccountAdminDetailForm from '../../../views/account/admin/form/AccountAdminDetailForm';

import { ADMIN_LEVEL_TYPE, FORM_DEFAULT_VALUE } from '../../../views/account/admin/common/constants';
import {
  ACCOUNT_ADMIN_PAGE_NAME,
  ACCOUNT_ADMIN_TAB_KEY,
  ACCOUNT_ADMIN_TAB_TITLE,
} from '../../../constants/service/app/accountAdminConstants';
import {
  AccountAdminCreateRequest,
  AccountAdminCreateResponse,
  FormMode,
} from '../../../views/account/admin/common/types/AccountAdminFormType';

import toastMsg from '../../../lib/toastMsg';
import { useAuth } from '../../../hooks/common/useAuth';
import PermissionTable from '../../../views/account/admin/list/PermissionTable';
import Tabs from '../../../components/Tabs/Tabs';

const Form = styled.form`
  width: 100%;
`;

interface RouteParams {
  id: string;
}

const AccountAdminDetail = (): JSX.Element => {
  const navigate = useNavigate();
  const { id } = useParams<keyof RouteParams>() as RouteParams;
  const { user } = useAuth();

  const isMasterAccount = user?.level === ADMIN_LEVEL_TYPE.MASTER;
  const isMyAccount = Number(id) === user?.id;

  const methods = useForm<AccountAdminCreateRequest>({
    defaultValues: FORM_DEFAULT_VALUE,
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const [currentTabKey, setCurrentTabKey] = useState(ACCOUNT_ADMIN_TAB_KEY.ADMIN_INFO);

  const { data: adminInfo } = useAccountAdminInformation({
    id: Number(id),
    options: {
      suspense: false,
      enabled: !!id,
      onSuccess: (data: AccountAdminCreateResponse) => {
        methods.reset(data?.data);
      },
      onError: (error) => toastMsg.error(error.message),
    },
  });

  const onSubmit = () => {
    navigate(`/account/admin/edit/${id}`);
  };

  return (
    <PageTemplate pageName={ACCOUNT_ADMIN_PAGE_NAME.ADMIN_DETAIL}>
      <FormProvider {...methods}>
        <Form id="account-admin-form" onSubmit={methods.handleSubmit(onSubmit)}>
          {(isMyAccount || isMasterAccount) && <AdminFormNavbar mode={FormMode.DETAIL} />}
          <Tabs
            className="admin-detail-tabs"
            onChange={(tabKey: number) => setCurrentTabKey(tabKey)}
            activeKey={currentTabKey}
            list={[
              {
                title: ACCOUNT_ADMIN_TAB_TITLE.ADMIN_INFO,
                content: <AccountAdminDetailForm />,
              },
              {
                title: ACCOUNT_ADMIN_TAB_TITLE.ADMIN_PERMISSION,
                content: <PermissionTable mode={FormMode.DETAIL} adminInfo={adminInfo} />,
                disabled: !isMasterAccount,
              },
            ]}
          />
        </Form>
      </FormProvider>
    </PageTemplate>
  );
};

export default AccountAdminDetail;
